import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { MLLocalDateUtil } from 'ml-portals-util-js';
import _ from 'lodash';
import appConfig from 'app-config';
import classNames from 'classnames';
import { ModalNext, ModalBody, ModalFooter, ModalHeader } from '@jutro/components';
import { useTranslator } from '@jutro/locale';

import metadata from './MLAddressVerificationModalComponent.metadata.json5';
import ampStyles from './MLAddressVerificationModalComponent.module.scss';
import peStyles from './MLPEAddressVerificationModalComponent.module.scss';
import messages from './MLAddressVerificationModalComponent.messages';

import { Button } from '@jutro/legacy/components';

function MLAddressVerificationModalComponent(props) {
    const MULTIPLE_ADDRESSES = 'MULTIPLE_ADDRESSES';
    const MANUALLY_CHANGED = 'MANUALLY_CHANGED';
    const UNABLE_TO_VALIDATE = 'UNABLE_TO_VALIDATE';
    const HOMEOWNERS = 'HOPHomeowners';
    const translator = useTranslator();
    const isGPA = appConfig.portalCode === 'GPA';
    const isQnB = appConfig.portalCode === 'QB';
    const styles = isGPA ? peStyles : ampStyles;

    const {
        typedAddress,
        addressOptions,
        mode,
        productType,
        isOpen,
        onReject,
        onResolve
    } = props;

    const [selectedIndex, setSelectedIndex] = useState(undefined);

    const modalSubTitle = () => {
        switch (mode) {
            case MULTIPLE_ADDRESSES:
                return translator(messages.multipleAddresses);
            case MANUALLY_CHANGED:
                return translator(messages.manuallyChanged);
            case UNABLE_TO_VALIDATE:
                return translator(messages.unableToValidate);
            default:
                return null;
        }
    };

    const onSelectAddressOption = useCallback((value) => {
        setSelectedIndex(value);
    }, [setSelectedIndex]);

    const getAddressValue = (addressField) => {
        if (_.isUndefined(addressField)) {
            return '';
        }
        return addressField;
    };

    const constructCityStatePostal = (enteredAddress) => {
        const city = getAddressValue(_.get(enteredAddress, 'value.city'));
        const separator = city === '' ? '' : ', ';
        const state = getAddressValue(_.get(enteredAddress, 'value.state'));
        const postalCode = getAddressValue(_.get(enteredAddress, 'value.postalCode'));
        return `${city}${separator}${state} ${postalCode} `;
    };

    const overrideProps = {
        insuredAddressLine1: {
            content: getAddressValue(_.get(typedAddress, 'addressLine1.value'))
        },
        insuredAddressLine2: {
            content: getAddressValue(_.get(typedAddress, 'addressLine2.value'))
        },
        cityStatePostalCode: {
            content: constructCityStatePostal(typedAddress)
        },
        verifyYourAddressDetails: {
            content: modalSubTitle()
        },
        addressVerificationContainer: {
            visible: mode === UNABLE_TO_VALIDATE
        },
        chooseAddressContainer: {
            visible: mode !== UNABLE_TO_VALIDATE
        },
        addressOptionRadioButton: {
            availableValues: addressOptions.map((singleAddress, index) => {
                const type = index > 0 ? '(Suggested address)' : '(Address Keyed)';
                return {
                    id: `${index}`,
                    displayName: `${singleAddress.addressLine1.value} ${singleAddress.addressLine2.value ? singleAddress.addressLine2.value + ' ' : ''}${constructCityStatePostal(singleAddress)}`,
                    subtitle: type,
                };
            }),
            className: isQnB ? 'addressOptionsQB radioButtonQnB': 'addressOptions',
            onValueChange: onSelectAddressOption,
            value: selectedIndex
        },
        insuredAddressLabel: {
            content: productType === HOMEOWNERS ? translator(messages.insuredResidence) : translator(messages.insuredAddress)
        }
    };

    const handleClose = useCallback(() => {
        onReject('editAddress');
    }, [onReject]);

    const extractPropsToCompare = (address) => {
        const {
            addressLine1, addressLine2, city, postalCode, state, country
        } = address;
        return ({
            addressLine1, addressLine2, city, postalCode, state, country
        });
    };

    const handleSave = () => {
        const localDate = MLLocalDateUtil.date2LocalDate(new Date());
        const selectedItem = addressOptions[selectedIndex];
        const typedAddressProps = extractPropsToCompare(typedAddress);
        let selectedAddress;
        if (mode !== UNABLE_TO_VALIDATE) {
            selectedAddress = extractPropsToCompare(selectedItem);
            if (_.isUndefined(selectedAddress.addressLine2)) {
                _.set(selectedAddress, 'addressLine2', '');
            }
        }

        if (_.isUndefined(typedAddressProps.addressLine2)) {
            typedAddressProps.addressLine2 = '';
        }

        if (!_.isEqual(selectedAddress, typedAddressProps) && (!_.isUndefined(selectedAddress))) {
            _.set(typedAddress, 'addressLine1', selectedAddress.addressLine1.value);
            _.set(typedAddress, 'addressLine2', selectedAddress.addressLine2.value);
            _.set(typedAddress, 'city', selectedAddress.city.value);
            _.set(typedAddress, 'country', selectedAddress.country.value);
            _.set(typedAddress, 'postalCode', selectedAddress.postalCode.value);
            _.set(typedAddress, 'state', selectedAddress.state.value);
            _.set(typedAddress, 'standardizeStatus_ML', true);
            _.set(typedAddress, 'standardizeOverride_ML', false);
            _.set(typedAddress, 'standardizeDate_ML', localDate);
            _.set(typedAddress, 'validateAddressStatus_ML', true);
            onResolve(selectedAddress);
        } else if (_.isEqual(selectedAddress, typedAddressProps)) {
            if (mode === MANUALLY_CHANGED || mode === MULTIPLE_ADDRESSES) {
                _.set(typedAddress, 'standardizeStatus_ML', false);
                _.set(typedAddress, 'standardizeOverride_ML', true);
                _.set(typedAddress, 'standardizeDate_ML', localDate);
                _.set(typedAddress, 'validateAddressStatus_ML', false);
                onResolve(selectedAddress);
            }
        } else if (_.isUndefined(selectedItem)) {
            _.set(typedAddress, 'standardizeStatus_ML', false);
            _.set(typedAddress, 'standardizeOverride_ML', true);
            _.set(typedAddress, 'standardizeDate_ML', localDate);
            _.set(typedAddress, 'validateAddressStatus_ML', false);
            onResolve(undefined);
        }
    };

    const actionBtnLabel = () => {
        if (mode === MULTIPLE_ADDRESSES || mode === MANUALLY_CHANGED) {
            return translator(messages.continue);
        }

        return translator(messages.useThisAddress);
    };

    const resolvers = {
        resolveClassNameMap: styles
    };

    const displayEditAddressButton = () => {
        if (mode !== UNABLE_TO_VALIDATE) {
            return styles.hideEditAddressButton;
        }
        return '';
    }
    return (
        <ModalNext
            className={styles['ml-modal-dialog']}
            isOpen={isOpen}
            onRequestClose={handleClose}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
        >
            <ModalHeader
                title={translator(messages.verifyYourAddress)}
                subtitle={modalSubTitle()}
                onClose={handleClose}
                contentLayout={{
                    component: 'div',
                    componentProps: {
                        className: styles['modal-header']
                    }
                }}
                titleLayout={{
                    component: 'grid',
                    componentProps: {
                        className: isQnB ? "headerTwo" : styles['modal-title']
                    }
                }}
            />
            <ModalBody contentLayout={{
                component: 'div',
                componentProps: {
                    className: styles['modal-body']
                }
            }}
            >
                <ViewModelForm
                    uiProps={metadata.pageContent}
                    model={typedAddress}
                    overrideProps={overrideProps}
                    classNameMap={resolvers.resolveClassNameMap}
                />
            </ModalBody>
            <ModalFooter contentLayout={{
                component: 'flex',
                componentProps: {
                    className: styles['modal-footer']
                }
            }}
            >
                <Button onClick={handleSave} disabled={mode !== UNABLE_TO_VALIDATE && _.isEmpty(selectedIndex)} type="tertiary" className={isQnB ? "primaryButton" : styles['ml-btn']}>
                    {actionBtnLabel()}
                </Button>
                <Button onClick={handleClose} type="neutral" className={classNames(styles['ml-btn'], displayEditAddressButton(), isQnB ? "secondaryButton widthSmall" : "")}>
                    {translator(messages.editAddress)}
                </Button>
            </ModalFooter>
        </ModalNext>
    );
}

MLAddressVerificationModalComponent.propTypes = {
    typedAddress: PropTypes.shape({}).isRequired,
    addressOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    mode: PropTypes.string.isRequired,
    isOpen: PropTypes.bool,
    onReject: PropTypes.func,
    onResolve: PropTypes.func,
    productType: PropTypes.string
};

MLAddressVerificationModalComponent.defaultProps = {
    onReject: _.noop,
    onResolve: _.noop,
    productType: '',
    isOpen: false
};


export default MLAddressVerificationModalComponent;
